import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import { addLog } from 'assets/components/feedback/Feedback';
import useForm from 'assets/components/form/hooks/Form';
import FileInputSimple from 'assets/components/inputs/file/FileInputSimple';
import TextInput from 'assets/components/inputs/text/TextInput';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { HttpStatus } from 'config/Api.Config';
import claimDocumentDeleteApi from 'models/claims/claimDocument/delete/Api.ClaimDocument.Delete';
import claimDocumentEditApi from 'models/claims/claimDocument/edit/Api.ClaimDocument.Edit';
import claimDocumentSaveApi from 'models/claims/claimDocument/save/Api.ClaimDocument.Save';
import ClaimStatusCode from 'models/enums/ClaimStatusCode';
import { Fragment, useEffect, useMemo, useState } from 'react';
import DocumentPreview from '../../../../../assets/components/other/DocumentPreview';
import { useClaimEdit } from '../ClaimEdit.Hooks';

export default function ClaimEditDocuments() {
  const { lang } = getLocales();
  const { reload, claim, isClaimAdmin, permissions, isClaimOwner } = useClaimEdit();

  const isClaimInCorrectStatus =
    claim?.statusCode === ClaimStatusCode.OPEN_PENDING ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_IN_PROGRESS ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_CUSTOMER ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_DEALERSHIP ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_OTHER ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_REPAIRFACILITY ||
    claim?.statusCode === ClaimStatusCode.OPEN_ACTIVE_SUBMITTED_SELLING_DEALER;

  const docForm = useForm<{
    claimId: number;
    documents: Array<Utils.FormData<Omit<Model.IClaimDocument, 'claimId'>>>;
  }>({
    default: { claimId: claim?.id, documents: [] },
    onSubmit: async ({ claimId, documents }) => {
      for (const document of documents) {
        if (document.formFile) {
          const res = await claimDocumentSaveApi({ ...document, claimId });
          if (res.status !== HttpStatus.ok) {
            addLog({ error: lang.uploadError });
          }
        }
      }
      docForm.reload();
      reload();
    },
    validation: (data, errors) => {
      if (data.documents?.some((it) => !it.name)) {
        errors.documents = { name: lang.required };
      }
    },
  });

  useEffect(() => {
    if (docForm?.data?.claimId !== claim?.id) {
      docForm.set({ claimId: claim?.id });
    }
  }, [claim?.id]);

  const [editingDoc, setEditingDoc] = useState<{
    id: number;
    name: string;
    notes?: string;
  } | null>(null);

  const editForm = useForm<{ name: string; notes?: string }>({
    default: { name: '', notes: '' },
    onSubmit: async ({ name, notes }) => {
      if (!claim?.id || !editingDoc?.id) return;

      const body: Api.Core.ClaimDocument.Edit.IRequest = {
        id: editingDoc.id,
        claimId: claim.id,
        name,
        notes,
      };

      const res = await claimDocumentEditApi(body);

      if (res.status !== HttpStatus.ok) {
        addLog({ error: lang.uploadError });
      }

      setEditingDoc(null);
      reload();
    },
    validation: (data, errors) => {
      if (!data.name) {
        errors.name = lang.required;
      }
    },
  });

  useEffect(() => {
    if (editingDoc) {
      editForm.set({
        name: editingDoc.name,
        notes: editingDoc.notes,
      });
    }
  }, [editingDoc?.id]);

  const onClaimDocumentEdit = (doc: { id: number; name: string; description?: string }) => {
    setEditingDoc({
      id: doc.id,
      name: doc.name,
      notes: doc.description,
    });
  };

  const onClaimDocumentDelete = async (documentId: number) => {
    await claimDocumentDeleteApi({ id: claim?.id, documentId });
    reload();
  };

  const isDownloadDisabled = !(isClaimAdmin || permissions?.DOWNLOAD_CLAIMDOCUMENT);
  const isEditDisabled =
    !isClaimInCorrectStatus || !(isClaimAdmin || (isClaimOwner && permissions?.EDIT_CLAIMDOCUMENT));
  const isDeleteDisabled =
    !isClaimInCorrectStatus || !(isClaimAdmin || (isClaimOwner && permissions?.DELETE_CLAIMDOCUMENT));
  const isNonEditable =
    !isClaimInCorrectStatus || !(isClaimAdmin || (isClaimOwner && permissions?.UPLOAD_CLAIMDOCUMENT));
  const isDisabled =
    !isClaimInCorrectStatus || docForm.hasErrors || docForm.isSubmitted || !docForm.data.documents?.length;

  const uploadedFiles = useMemo(
    () =>
      docForm.data?.documents?.map((doc) => ({
        original: doc.formFile,
        type: doc.formFile.type as Utils.File.MimeType,
        dataUrl: window.URL.createObjectURL(doc.formFile),
        name: doc.name,
        notes: doc.notes,
      })),
    [docForm.data.documents]
  );

  return (
    <>
      <h2 id="documents">{lang.documents}</h2>
      <br />
      <InputWrapper>
        <FileInputSimple
          name="files"
          value={uploadedFiles}
          onChange={({ files }) => {
            docForm.update({
              documents: [
                ...(docForm.data.documents ?? []),
                ...files.map((file) => ({
                  formFile: file.original,
                  name: file.original?.name,
                })),
              ],
            });
          }}
          label={lang.documentUpload}
          disabled={isNonEditable}
        />
        <Button
          render={lang.upload}
          onClick={docForm.submit}
          disabled={isDisabled}
          active={!docForm.hasErrors}
          class={ThemeButtonCircle}
          style={{ flexShrink: 0 }}
        />
      </InputWrapper>
      <DataMap
        data={docForm.data.documents ?? []}
        render={({ data: uploadedFile, index }) => (
          <Fragment key={index}>
            <br />
            <br />
            <InputWrapper>
              <TextInput
                name="name"
                label={lang.name}
                value={uploadedFile.name}
                error={!uploadedFile.name ? lang.required : undefined}
                onChange={(data) => {
                  docForm.data.documents[index] = { ...docForm.data.documents[index], ...data };
                  docForm.update({ documents: [...docForm.data.documents] });
                }}
                disabled={isNonEditable}
              />
              <TextInput
                name="notes"
                label={lang.notes}
                value={uploadedFile.notes}
                onChange={(data) => {
                  docForm.data.documents[index] = { ...docForm.data.documents[index], ...data };
                  docForm.update({ documents: [...docForm.data.documents] });
                }}
                disabled={isNonEditable}
              />
            </InputWrapper>
          </Fragment>
        )}
      />
      <br />

      <DocumentPreview
        documents={
          claim?.claimDocuments?.map((it) => ({
            id: it.id,
            url: it.mediaFile.url,
            name: it.name,
            extension: it.mediaFile?.extension,
            description: it.notes,
            mimeType: it.mediaFile?.contentType,
            storedFilename: it.mediaFile?.name,
            documentType: lang.document,
            onDelete: () => onClaimDocumentDelete(it.id),
            onEdit: (doc) => onClaimDocumentEdit(doc),
          })) ?? []
        }
        isEditDisabled={isEditDisabled}
        isDeleteDisabled={isDeleteDisabled}
        isDownloadDisabled={isDownloadDisabled}
        showGallery={true}
      >
        {editingDoc && (
          <div style={{ marginLeft: Theme.Size.XL, marginRight: Theme.Size.XL }}>
            <h3>{`${lang.edit} ${lang.documents}`}</h3>
            <br />
            <InputWrapper>
              <TextInput
                name="name"
                label={lang.name}
                value={editForm.data.name}
                error={editForm.errors?.name}
                onChange={(data) => editForm.update(data)}
                disabled={editForm.isSubmitted}
              />
              <TextInput
                name="notes"
                label={lang.notes}
                value={editForm.data.notes}
                onChange={(data) => editForm.update(data)}
                disabled={editForm.isSubmitted}
              />
              <Button
                render={lang.save}
                onClick={editForm.submit}
                active={!editForm.hasErrors}
                disabled={editForm.hasErrors || editForm.isSubmitted}
                class={ThemeButtonCircle}
                style={{ flexShrink: 0 }}
              />
              <Button
                render={lang.cancel}
                onClick={() => setEditingDoc(null)}
                class={ThemeButtonCircle}
                style={{ flexShrink: 0 }}
              />
            </InputWrapper>
            <br />
            <br />
          </div>
        )}
      </DocumentPreview>
    </>
  );
}
